/**
 * useSpotlightTimers — when the "Show me" highlight turns itself on and off.
 *
 * Two rules, deliberately separate:
 *
 *  1. **Auto-show on arrival.** Reaching a step highlights its target without a
 *     click, so the common case needs no interaction. Fires once per arrival
 *     (including a context regression back to an earlier step), and not again
 *     after it has timed out — so the page never stays decorated.
 *
 *  2. **Time out from when the target is FOUND**, not from when the step opened.
 *     A step that navigates first would otherwise spend its whole window on a
 *     page that hasn't rendered the target yet, and the user would see nothing.
 *
 * The `spotlightOn` state itself lives in the caller because `useAnchor` needs it
 * to decide whether to track — so this hook takes the value and its setter rather
 * than owning them. Extracted so the timing is testable on its own: HelpGuide
 * can't be mounted outside a real session (its role gate returns null).
 *
 * @param {object} args
 * @param {boolean} args.spotlightOn
 * @param {(v: boolean) => void} args.setSpotlightOn
 * @param {string|null} args.stepId      — changes on every step arrival
 * @param {string|null} args.flowId
 * @param {boolean} args.finished
 * @param {boolean} args.collapsed       — no point firing when nothing renders
 * @param {boolean} args.anchorFound     — has the target actually resolved?
 * @param {boolean} args.hasStep
 * @param {number} [args.timeoutMs]
 */

import { useEffect } from 'react';

export const SPOTLIGHT_MS = 6000;

export default function useSpotlightTimers({
    spotlightOn,
    setSpotlightOn,
    stepId,
    flowId,
    finished,
    collapsed,
    anchorFound,
    hasStep,
    timeoutMs = SPOTLIGHT_MS,
}) {
    // 1. Auto-show on arrival at a step.
    useEffect(() => {
        if (!hasStep || finished || collapsed) return;
        setSpotlightOn(true);
        // Keyed on the step: once per arrival, never re-fired after a timeout.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stepId, flowId, finished, collapsed, hasStep]);

    // 2. Clear it once the user has had a few seconds to see it.
    useEffect(() => {
        if (!spotlightOn || !anchorFound) return undefined;
        const t = setTimeout(() => setSpotlightOn(false), timeoutMs);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [spotlightOn, anchorFound, stepId, timeoutMs]);
}
